import { useId } from 'react';
import { cn } from '@/lib/utils';

export interface RadioCardOption<T extends string = string> {
  value: T;
  title: string;
  description?: string;
  /** Shown greyed out and can't be picked, e.g. an out-of-stock colour. */
  disabled?: boolean;
}

interface RadioCardGroupProps<T extends string> {
  /** Question above the options, e.g. "Why are you returning it?". */
  label: string;
  options: RadioCardOption<T>[];
  value?: T;
  onChange: (value: T) => void;
  /**
   * Fired when an option is chosen on purpose — tapped/clicked, or Space/Enter — including the one
   * already selected. Not fired while arrowing through the list. Use it to move on straight away.
   */
  onPick?: (value: T) => void;
  className?: string;
}

/** Card holding a single-choice list of tappable rows (Figma "Radio row"). */
function RadioCardGroup<T extends string>({ label, options, value, onChange, onPick, className }: RadioCardGroupProps<T>) {
  const name = useId();
  return (
    <fieldset className={cn('tw-flex tw-flex-col tw-gap-2.5 tw-rounded-2xl tw-border tw-border-ds-border-default tw-bg-ds-surface tw-p-5', className)}>
      <legend className="tw-float-left tw-mb-0.5 tw-w-full tw-text-[15px] tw-font-semibold tw-leading-5 tw-text-ds-text-primary">{label}</legend>
      {options.map((option) => {
        const selected = option.value === value;
        return (
          <label
            key={option.value}
            className={cn(
              'tw-flex tw-items-center tw-gap-3 tw-rounded-xl tw-p-4 tw-transition-colors',
              'has-[:focus-visible]:tw-ring-2 has-[:focus-visible]:tw-ring-ds-brand has-[:focus-visible]:tw-ring-offset-2 has-[:focus-visible]:tw-ring-offset-ds-surface',
              selected
                ? 'tw-border-[1.5px] tw-border-ds-brand tw-bg-ds-brand-tint'
                : 'tw-border tw-border-ds-border-default tw-bg-ds-surface',
              option.disabled ? 'tw-cursor-not-allowed' : 'tw-cursor-pointer hover:tw-border-ds-border-strong',
            )}
          >
            <input
              type="radio"
              name={name}
              value={option.value}
              checked={selected}
              disabled={option.disabled}
              onChange={() => onChange(option.value)}
              // A pointer click has detail ≥ 1; arrow-key selection also fires a click, with detail 0.
              onClick={(e) => e.detail > 0 && onPick?.(option.value)}
              onKeyDown={(e) => {
                if (e.key !== ' ' && e.key !== 'Enter') return;
                e.preventDefault();
                onChange(option.value);
                onPick?.(option.value);
              }}
              className="tw-sr-only"
            />
            <span
              aria-hidden
              className={cn(
                'tw-h-5 tw-w-5 tw-shrink-0 tw-rounded-full tw-bg-ds-surface',
                selected ? 'tw-border-[6px] tw-border-ds-brand' : 'tw-border-[1.5px] tw-border-ds-border-strong',
                option.disabled && 'tw-opacity-50',
              )}
            />
            <span className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-0.5">
              <span className={cn('tw-text-[15px] tw-font-semibold tw-leading-5', option.disabled ? 'tw-text-ds-text-tertiary' : 'tw-text-ds-text-primary')}>
                {option.title}
              </span>
              {option.description && (
                <span className={cn('tw-text-[13px] tw-leading-[18px]', option.disabled ? 'tw-text-ds-text-tertiary' : 'tw-text-ds-text-secondary')}>
                  {option.description}
                </span>
              )}
            </span>
          </label>
        );
      })}
    </fieldset>
  );
}

export default RadioCardGroup;
